import { defineComponent as _defineComponent } from 'vue'
import { VideoMarket, VideoV1 } from '@ilteducation/content-registry-types';
import { ref } from 'vue';
import type { Area, Category } from '@ilteducation/content-ref-data-types';
import customProvider, { Params } from '@/data-provider/customProvider';
import { useExcelDownload } from '@/comp/use-excel-download';
import { getAllRefData } from '@/data-provider/refDataProvider';
import { getTitle } from '@/foundations/helpers/videoUtils';
import { useGrades } from '@/comp/use-grades';


export default /*#__PURE__*/_defineComponent({
  __name: 'DownloadVideoStats',
  setup(__props) {

const { exportExcel } = useExcelDownload();
const { gradeAbbreviation } = useGrades();

const loading = ref(false);

async function fetchVideos(): Promise<VideoV1[]> {
  const params: Params = {
    filter: {},
    sort: [],
    pagination: {
      perPage: 100,
      page: 1,
    },
    include: undefined,
  };

  const getData = () => customProvider().getList('videos', params);
  const videos: VideoV1[] = [];
  let done = true;
  do {
    // eslint-disable-next-line no-await-in-loop
    const { data, total } = await getData();
    videos.push(...(data as VideoV1[]));
    done = videos.length >= (total ?? 0);
    params.pagination.page! += 1;
  } while (!done);
  return videos;
}

async function createExcel() {
  try {
    loading.value = true;
    const videos = await fetchVideos();
    const categories: Category[] = await getAllRefData('categories');
    const areas: Area[] = await getAllRefData('areas');
    const allRealms = ['se', 'fi', 'no'];

    const getSubjectById = (id: string) => categories.find((s) => s.id === id);
    const getAreaById = (id: string) => areas.find((s) => s.id === id);

    const getVideosForMarket = (video: VideoV1, market: VideoMarket): { category: Category; area: Area }[] => {
      return (video.videos || [])
        ?.filter((v) => v.market === market && !!v.categoryId && !!v.areaId)
        .map((v) => ({
          category: getSubjectById(v.categoryId),
          area: getAreaById(v.areaId),
        }))
        .filter((ac): ac is { category: Category; area: Area } => ac.category !== undefined);
    };
    const videoUrl = (video: VideoV1, market: VideoMarket, category: Category, area: Area) => {
      return `https://${process.env.VUE_APP_ENVIRONMENT === 'production' ? 'app' : 'app-dev'}.${
        market === 'se' ? 'begreppa' : 'manabua'
      }.${market}/videos/${category.slug}/${area.slug}/${video.slug[market]}`;
    };

    const convertedVideos = videos.map((video) => ({
      ...video,
      title: getTitle(video),
      subtitles: video.subtitles.reduce((acc, subtitle) => {
        // eslint-disable-next-line no-param-reassign
        acc[subtitle.languageCode] = 'yes';
        return acc;
      }, {} as Record<string, string>),
      narrations: video.narrations.reduce((acc, narration) => {
        // eslint-disable-next-line no-param-reassign
        acc[narration.languageCode] = 'yes';
        return acc;
      }, {} as Record<string, string>),
      availabilityRealms: video.availabilityRealms.reduce((acc, realm) => {
        // eslint-disable-next-line no-param-reassign
        acc[realm] = 'yes';
        return acc;
      }, {} as Record<string, string>),
      grades: video.categories.reduce((acc, cId) => {
        const category = categories.find((c) => c.id === cId);
        const realm = category?.realms?.[0];

        if (category && realm && category.kind === 'grade') {
          acc[realm] = acc[realm] ? `${acc[realm]}, ${category.categoryCode}` : category.categoryCode;
        }
        return acc;
      }, {} as Record<string, string>),
      descriptions: video.description.reduce((acc, description) => {
        // eslint-disable-next-line no-param-reassign
        acc[description.languageCode] = 'yes';
        return acc;
      }, {} as Record<string, string>),
      summaries: video.summary.reduce((acc, summary) => {
        // eslint-disable-next-line no-param-reassign
        acc[summary.languageCode] = 'yes';
        return acc;
      }, {} as Record<string, string>),
      introfilmsUid: Object.entries(video.introfilmsUids).reduce((acc, [realm, uids]) => {
        // eslint-disable-next-line no-param-reassign
        acc[realm] = Object.values(uids).join(', ');
        return acc;
      }, {} as Record<string, string>),
      urls: allRealms.reduce((acc, realm) => {
        // eslint-disable-next-line no-param-reassign
        acc[realm] = getVideosForMarket(video, realm as VideoMarket)
          .map(({ category, area }) => videoUrl(video, realm as VideoMarket, category, area))
          .join(', ');
        return acc;
      }, {} as Record<string, string>),
      categoryAreasPerMarket: allRealms.reduce((acc, realm) => {
        // eslint-disable-next-line no-param-reassign
        acc[realm] = video.orderedAreas
          .map((oa) => oa.id)
          .map((areaId) => {
            const area = areas.find((a) => a.id === areaId);
            const category = getSubjectById(area?.categoryId ?? '');
            return { category, area };
          })
          .filter(({ category }) => category?.realms?.includes(realm))
          .map(({ category, area }) => `${category ? category.category : '-'} / ${area ? area.name : '-'}`)
          .join(', ');
        return acc;
      }, {} as Record<string, string>),
    }));

    // find all languages used in all videos
    const allLanguages = convertedVideos.reduce((acc, video) => {
      Object.keys(video.subtitles).forEach((lang) => {
        if (!acc.includes(lang)) {
          acc.push(lang);
        }
      });
      Object.keys(video.narrations).forEach((lang) => {
        if (!acc.includes(lang)) {
          acc.push(lang);
        }
      });
      return acc;
    }, [] as string[]);
    const allDescriptionLanguages = convertedVideos.reduce((acc, video) => {
      Object.keys(video.descriptions).forEach((lang) => {
        if (!acc.includes(lang)) {
          acc.push(lang);
        }
      });
      return acc;
    }, [] as string[]);
    const allSummaryLanguages = convertedVideos.reduce((acc, video) => {
      Object.keys(video.summaries).forEach((lang) => {
        if (!acc.includes(lang)) {
          acc.push(lang);
        }
      });
      return acc;
    }, [] as string[]);

    exportExcel(
      [
        { label: 'Id', field: 'id' },
        { label: 'Internal Film ID', field: 'filmId' },
        { label: 'Title', field: 'title' },
        ...allRealms.map((realm) => ({
          label: `Introfilms UIDs in ${realm.toUpperCase()}`,
          field: `introfilmsUid.${realm}`,
        })),
        ...allRealms.map((realm) => ({
          label: `Subjects in ${realm.toUpperCase()}`,
          field: `categoryAreasPerMarket.${realm}`,
        })),
        ...allRealms.map((realm) => ({
          label: `Grades in ${realm.toUpperCase()}`,
          field: `grades.${realm}`,
        })),
        ...allRealms.map((realm) => ({
          label: `Active in ${realm.toUpperCase()}`,
          field: `availabilityRealms.${realm}`,
        })),
        ...allRealms.map((realm) => ({
          label: `URLs in ${realm.toUpperCase()}`,
          field: `urls.${realm}`,
        })),
        ...allDescriptionLanguages.map((lang) => ({
          label: `Description in ${lang.toUpperCase()}`,
          field: `descriptions.${lang}`,
        })),
        ...allSummaryLanguages.map((lang) => ({
          label: `Summary in ${lang.toUpperCase()}`,
          field: `summaries.${lang}`,
        })),
        ...allLanguages.map((lang) => ({ label: `Narration ${lang}`, field: `narrations.${lang}` })),
        ...allLanguages.map((lang) => ({ label: `Subtitles ${lang}`, field: `subtitles.${lang}` })),
      ],
      convertedVideos,
      'manabua-videos',
    );
  } finally {
    loading.value = false;
  }
}

return { __sfc: true,exportExcel, gradeAbbreviation, loading, fetchVideos, createExcel }
}

})